// Componentes e utilitários de UI compartilhados entre as áreas.
//   * toast(): notificação efêmera (mesmo elemento #toast do HTML).
//   * kpiHTML(): cartão de KPI (HTML string).
//   * drop(): dropdown custom (.dropwrap/.dropbtn/.dropmenu) com contagem.
//   * debounce(): atraso para saves de inputs de texto (~400ms).
//   * buildTable(): tabela editável genérica (contas/perfis/proxys) ligada ao
//     Supabase, trabalhando com OBJETOS nomeados e id por linha.
import { esc, svgDel, svgCopy, maskBRDate, dateFieldState } from '../lib/format.js'
import { marcarSujo, marcarLimpo } from '../lib/save-status.js'

// Atraso padrão para persistir inputs de texto (evita 1 save por tecla).
export const DEBOUNCE_MS = 400

// Notificação efêmera no rodapé. `erro=true` mostra em vermelho, com ícone de
// alerta e por mais tempo (feedback de falha mais visível).
export function toast(msg, erro = false) {
  const t = document.getElementById('toast')
  if (!t) return
  const icone = erro
    ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 9v4m0 4h.01M10.3 3.9l-8 14A2 2 0 004 21h16a2 2 0 001.7-3l-8-14a2 2 0 00-3.4 0z"/></svg>'
    : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 13l4 4L19 7"/></svg>'
  t.className = 'toast' + (erro ? ' erro' : '')
  t.innerHTML = icone + msg
  t.classList.add('show')
  clearTimeout(toast._timer)
  toast._timer = setTimeout(() => t.classList.remove('show'), erro ? 3500 : 2000)
}

// Cartão de KPI (idêntico ao legado).
export function kpiHTML(lab, val, sub, cls, style) {
  return (
    '<div class="kpi ' +
    (cls || '') +
    '"><div class="lab">' +
    lab +
    '</div><div class="val" style="' +
    (style || '') +
    '">' +
    val +
    '</div>' +
    (sub ? '<div class="sub">' + sub + '</div>' : '') +
    '</div>'
  )
}

// Cria um debouncer simples reutilizável por chave (ex.: id|campo).
export function debounce(fn, ms = DEBOUNCE_MS) {
  let timer = null
  const run = (...args) => {
    clearTimeout(timer)
    timer = setTimeout(() => {
      timer = null
      fn(...args)
    }, ms)
  }
  // flush(): dispara imediatamente se houver algo pendente (ex.: ao sair do campo).
  run.flush = (...args) => {
    if (timer) {
      clearTimeout(timer)
      timer = null
      fn(...args)
    }
  }
  return run
}

// Dropdown custom com rótulo, opções e contagem por opção.
// onPick(valor) é chamado ao escolher ('' = limpar filtro).
export function drop(wrapId, label, sel, counts, opts, onPick) {
  const dw = document.getElementById(wrapId)
  if (!dw) return
  const cur = sel || ''
  let items = '<div class="dropitem' + (cur === '' ? ' sel' : '') + '" data-f="">' + label + '</div>'
  items += opts
    .map(
      o =>
        '<div class="dropitem' +
        (cur === o ? ' sel' : '') +
        '" data-f="' +
        esc(o) +
        '">' +
        esc(o) +
        ' <span class="qt">' +
        (counts[o] || 0) +
        '</span></div>'
    )
    .join('')
  dw.innerHTML =
    '<button class="dropbtn' +
    (cur ? ' has' : '') +
    '"><span class="cv">' +
    esc(cur || label) +
    '</span><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M6 9l6 6 6-6"/></svg></button><div class="dropmenu">' +
    items +
    '</div>'
  const btn = dw.querySelector('.dropbtn')
  btn.onclick = ev => {
    ev.stopPropagation()
    document.querySelectorAll('.dropwrap').forEach(x => {
      if (x !== dw) x.classList.remove('open')
    })
    dw.classList.toggle('open')
  }
  dw.querySelectorAll('.dropitem').forEach(it => {
    it.onclick = () => {
      dw.classList.remove('open')
      onPick(it.dataset.f)
    }
  })
}

// Tabela editável genérica para coleções de OBJETOS.
//
// cfg = {
//   data: array de objetos (cada um com .id),
//   bodyId, countId, search,
//   filter(obj) -> boolean (opcional),
//   searchFields: [campo,...] (campos considerados na busca),
//   dateCol: campo de data a "carimbar" no edit (opcional),
//   cols: [{
//     field: nome do campo no objeto,
//     type: 'text'|'select'|'dynselect'|'status'|'readonly',
//     cls, opts, optsFn, copy
//   }],
//   onPersist(obj, campo, valor) -> Promise  // grava 1 campo no Supabase
//   onAddPosicao, onDelete(obj) -> Promise    // remove no Supabase
//   stamp(obj) -> aplica data de hoje no campo dateCol (opcional)
//   onChange() -> recalcula KPIs/contadores e re-renderiza filtros (opcional)
//   rerender() -> re-renderiza a tabela inteira (para selects)
// }
export function buildTable(cfg) {
  const body = document.getElementById(cfg.bodyId)
  if (!body) return
  body.innerHTML = ''
  const term = (cfg.search || '').toLowerCase()
  let shown = 0

  cfg.data.forEach(obj => {
    if (term) {
      const hay = (cfg.searchFields || [])
        .map(f => obj[f] ?? '')
        .join(' ')
        .toLowerCase()
      if (!hay.includes(term)) return
    }
    if (cfg.filter && !cfg.filter(obj)) return
    shown++

    const tr = document.createElement('tr')
    cfg.cols.forEach(col => {
      const td = document.createElement('td')
      const valor = obj[col.field] ?? ''

      if (col.type === 'select') {
        const sel = document.createElement('select')
        sel.className = 'cell'
        col.opts.forEach(o => {
          const op = document.createElement('option')
          op.value = o
          op.textContent = o
          if (valor === o) op.selected = true
          sel.appendChild(op)
        })
        sel.onchange = async () => {
          obj[col.field] = sel.value
          if (cfg.stamp) cfg.stamp(obj)
          await cfg.onPersist(obj, col.field, sel.value)
          cfg.rerender()
          if (cfg.onChange) cfg.onChange()
        }
        td.appendChild(sel)
      } else if (col.type === 'dynselect') {
        const sel = document.createElement('select')
        sel.className = 'cell' + (col.cls ? ' ' + col.cls : '')
        const opts = ['', ...(col.optsFn ? col.optsFn() : [])]
        if (valor && !opts.includes(valor)) opts.push(valor)
        opts.forEach(o => {
          const op = document.createElement('option')
          op.value = o
          op.textContent = o || '—'
          if (valor === o) op.selected = true
          sel.appendChild(op)
        })
        sel.onchange = async () => {
          obj[col.field] = sel.value
          if (cfg.stamp) cfg.stamp(obj)
          await cfg.onPersist(obj, col.field, sel.value)
          cfg.rerender()
          if (cfg.onChange) cfg.onChange()
        }
        td.appendChild(sel)
      } else if (col.type === 'status') {
        const v = valor || ''
        // A própria pílula (tag) abre o dropdown: um <select> transparente fica
        // sobreposto e captura o clique, abrindo as opções nativas. Sem duplicar
        // tag + select embaixo.
        const wrap = document.createElement('span')
        wrap.className = 'st-wrap'
        const span = document.createElement('span')
        span.className = 'st ' + v.replace(/\s+/g, '')
        span.innerHTML = esc(v || '—') + '<span class="st-caret">▾</span>'
        const sel = document.createElement('select')
        const opts = col.opts || []
        const list = opts.includes(v) ? opts : v ? [v, ...opts] : opts
        list.forEach(o => {
          const op = document.createElement('option')
          op.value = o
          op.textContent = o || '—'
          if (v === o) op.selected = true
          sel.appendChild(op)
        })
        sel.onchange = async () => {
          obj[col.field] = sel.value
          if (cfg.stamp) cfg.stamp(obj)
          await cfg.onPersist(obj, col.field, sel.value)
          if (cfg.onChange) cfg.onChange()
          cfg.rerender()
        }
        wrap.appendChild(span)
        wrap.appendChild(sel)
        td.appendChild(wrap)
      } else if (col.type === 'readonly') {
        const span = document.createElement('span')
        span.className = 'cell' + (col.cls ? ' ' + col.cls : '')
        span.style.color = 'var(--mut)'
        span.textContent = valor || '—'
        span.dataset.role = 'date-' + obj.id
        td.appendChild(span)
      } else {
        // input de texto — persiste com debounce e SEM re-render (preserva foco)
        const inp = document.createElement('input')
        inp.className = 'cell' + (col.cls ? ' ' + col.cls : '')
        inp.value = valor
        if (col.mask === 'date') {
          inp.inputMode = 'numeric'
          inp.maxLength = 10
          inp.placeholder = 'dd/mm/aaaa'
        }
        const salvar = debounce(async () => {
          const snapshot = inp.value
          if (cfg.stamp) cfg.stamp(obj)
          await cfg.onPersist(obj, col.field, inp.value)
          if (inp.value === snapshot) marcarLimpo(inp) // confirmado (sem edição nova)
          if (cfg.onChange) cfg.onChange()
        })
        inp.oninput = () => {
          if (col.mask === 'date') inp.value = maskBRDate(inp.value)
          obj[col.field] = inp.value
          if (col.mask === 'date') {
            const { salvar: podeSalvar, invalida } = dateFieldState(inp.value)
            inp.classList.toggle('cell-invalid', invalida)
            if (!podeSalvar) {
              marcarLimpo(inp) // data incompleta/ inválida não fica "pendente"
              return
            }
          }
          // atualiza o "carimbo" de data exibido (coluna readonly) sem re-render
          if (cfg.dateCol) {
            const ds = body.querySelector('[data-role="date-' + obj.id + '"]')
            if (ds) ds.textContent = obj[cfg.dateCol] || '—'
          }
          marcarSujo(inp)
          salvar()
        }
        inp.onblur = () => salvar.flush() // ao sair do campo, salva imediatamente
        td.appendChild(inp)
        if (col.copy) {
          const cp = document.createElement('span')
          cp.className = 'copy'
          cp.innerHTML = svgCopy
          cp.title = 'Copiar'
          cp.onclick = () => {
            if (navigator.clipboard) navigator.clipboard.writeText(obj[col.field] || '')
            toast('Copiado')
          }
          td.style.whiteSpace = 'nowrap'
          td.appendChild(cp)
        }
      }
      tr.appendChild(td)
    })

    // coluna de exclusão
    const tdD = document.createElement('td')
    const del = document.createElement('span')
    del.className = 'del'
    del.innerHTML = svgDel
    del.onclick = async () => {
      const i = cfg.data.indexOf(obj)
      if (i === -1) return
      cfg.data.splice(i, 1)
      cfg.rerender()
      if (cfg.onChange) cfg.onChange()
      try {
        await cfg.onDelete(obj)
      } catch (e) {
        cfg.data.splice(i, 0, obj) // rollback: o banco não confirmou a exclusão
        cfg.rerender()
        if (cfg.onChange) cfg.onChange()
        toast('Erro ao excluir', true)
      }
    }
    tdD.appendChild(del)
    tr.appendChild(tdD)
    body.appendChild(tr)
  })

  if (cfg.countId) {
    const c = document.getElementById(cfg.countId)
    if (c)
      c.textContent =
        shown + (shown !== cfg.data.length ? ' / ' + cfg.data.length : '') + ' linhas'
  }
}

// Fecha qualquer dropdown aberto ao clicar fora (registrado uma vez no main).
export function ligarFechamentoDropdowns() {
  document.addEventListener('click', e => {
    if (!e.target.closest('.dropwrap'))
      document.querySelectorAll('.dropwrap').forEach(x => x.classList.remove('open'))
  })
}
